// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Alert, Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import CustomPropertiesInput from '../../../components/Form/CustomPropertiesInput';
import TextInput from '../../../components/Form/TextInput';
import {
  PropertyEntry,
  getPropertyErrors,
  propertyEntriesToMap,
} from '../../../components/Form/customPropertiesValidation';
import {
  getValidateAlphanumericMessage,
  getValidateVisibleMessage,
} from '../../../components/Form/fieldValidation';
import useCreateDtccTokenInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useCreateDtccTokenInstrumentConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistryInstrumentConfigurationFactory from '../../../hooks/queries/registry/configuration/useRegistryInstrumentConfigurationFactory';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import { useGetAllInstrumentConfigurations } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { getInstrumentOverlapWarning } from '../../../utils/instrumentOverlap';
import { ERR_CRE_INST_CONF, SUC_CRE_INST_CONF } from '../../../utils/strings';
import InstrumentIssueTypeSelect from './InstrumentIssueTypeSelect';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

const CreateDtccTokenInstrumentConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [tokenName, setTokenName] = useState<string>('');
  const [tokenSymbol, setTokenSymbol] = useState<string>('');
  const [cusip, setCusip] = useState<string>('');
  const [shortDescription, setShortDescription] = useState<string>('');
  const [instrumentIssueType, setInstrumentIssueType] = useState<string>('');
  const [properties, setProperties] = useState<PropertyEntry[]>([]);

  const registrarService = useRegistrarService();
  const mutate = useMutate();
  const createDtccTokenInstrumentConfiguration = useCreateDtccTokenInstrumentConfiguration();
  const instrumentConfigurationFactory = useRegistryInstrumentConfigurationFactory();
  const instrumentConfigurations = useGetAllInstrumentConfigurations();

  const resetState = useCallback(() => {
    setTokenName('');
    setTokenSymbol('');
    setCusip('');
    setShortDescription('');
    setInstrumentIssueType('');
    setProperties([]);
  }, [setTokenName, setTokenSymbol, setCusip, setShortDescription, setProperties]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!registrarService.data || !instrumentConfigurationFactory.data) return;
    const [issueType, issueSubtype] = instrumentIssueType.toLowerCase().split('-');
    const payload = {
      registrarServiceCid: registrarService.data.contractId,
      instrumentConfigurationFactoryCid: instrumentConfigurationFactory.data.contractId,
      tokenName,
      tokenSymbol,
      cusip,
      shortDescription,
      issueType,
      issueSubtype,
      properties: propertyEntriesToMap(properties),
    };
    await mutate(
      createDtccTokenInstrumentConfiguration,
      payload,
      SUC_CRE_INST_CONF,
      ERR_CRE_INST_CONF,
    );
    handleClose();
  }, [
    registrarService.data,
    instrumentConfigurationFactory.data,
    createDtccTokenInstrumentConfiguration,
    tokenName,
    tokenSymbol,
    cusip,
    shortDescription,
    instrumentIssueType,
    properties,
    handleClose,
    mutate,
  ]);

  const { isLoading } = registrarService;
  if (isLoading || !instrumentConfigurationFactory.data) return null;

  const tokenNameValidationMessage = getValidateVisibleMessage(tokenName);
  const cusipValidationMessage = getValidateAlphanumericMessage(cusip);
  const tokenSymbolValidationMessage = getValidateAlphanumericMessage(tokenSymbol);

  const overlapWarning = getInstrumentOverlapWarning(
    instrumentConfigurations.data?.instrumentConfigurations,
    { tokenName, tokenSymbol, cusip },
  );

  const propertyErrors = getPropertyErrors(properties);
  const hasPropertyErrors = propertyErrors.some((e) => e !== '');

  const disableSubmit =
    !tokenName ||
    !tokenSymbol ||
    !cusip ||
    !instrumentIssueType ||
    hasPropertyErrors ||
    !!tokenNameValidationMessage ||
    !!cusipValidationMessage ||
    !!tokenSymbolValidationMessage;

  return (
    <FormDialog
      open={open}
      title="Create DTCC Token"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Token Name"
          value={tokenName}
          setValue={(value) => {
            setTokenName(value);
          }}
          required
          validationMessage={tokenNameValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Token Symbol"
          value={tokenSymbol}
          setValue={setTokenSymbol}
          required
          validationMessage={tokenSymbolValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="CUSIP"
          value={cusip}
          setValue={setCusip}
          required
          validationMessage={cusipValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <InstrumentIssueTypeSelect
          value={instrumentIssueType ?? ''}
          setValue={setInstrumentIssueType}
        />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Short Description"
          value={shortDescription}
          setValue={(value) => {
            setShortDescription(value);
          }}
        />
      </Box>
      <Box mb={3}>
        <CustomPropertiesInput
          properties={properties}
          setProperties={setProperties}
          errors={propertyErrors}
        />
      </Box>
      {overlapWarning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {overlapWarning}
        </Alert>
      )}
    </FormDialog>
  );
};

export default CreateDtccTokenInstrumentConfigurationDialog;
